import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Gift, CheckCircle2, Sparkles, TrendingUp } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExitIntentPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Check if user has already seen the popup
    const hasSeenPopup = localStorage.getItem('exitIntentShown');
    const hasSubmittedEmail = localStorage.getItem('emailSubmitted');
    
    if (hasSeenPopup || hasSubmittedEmail) {
      return;
    }

    // Detect exit intent (mouse leaving from top of page)
    const handleMouseLeave = (e) => {
      // Only trigger if mouse is moving toward top of viewport
      if (e.clientY <= 50 && !isVisible) {
        setIsVisible(true);
        localStorage.setItem('exitIntentShown', 'true');
      }
    };

    // Only enable on desktop (better UX)
    if (window.innerWidth > 768) {
      document.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isVisible]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !name) {
      toast({
        title: "Missing Information",
        description: "Please enter your name and email address.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      // Submit to backend
      await axios.post(`${BACKEND_URL}/api/leads`, {
        name,
        email,
        source: 'exit-intent-popup'
      });

      localStorage.setItem('emailSubmitted', 'true');
      setSubmitted(true);
      
      toast({
        title: "Success! 🎉",
        description: "Check your email for the free $10K Affiliate Blueprint!",
      });

      // Close popup after 3 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 3000);

    } catch (error) {
      console.error('Error capturing lead:', error);
      toast({
        title: "Oops!",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-in fade-in duration-300"
        onClick={handleClose}
      />

      {/* Popup */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full pointer-events-auto animate-in zoom-in-95 duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors z-10"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>

          {submitted ? (
            // Success State
            <div className="p-12 text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Check Your Email! 📧
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                We've sent the <strong>$10K Affiliate Blueprint</strong> to <strong>{email}</strong>
              </p>
              <p className="text-gray-600">
                If you don't see it in a few minutes, check your spam folder.
              </p>
            </div>
          ) : (
            // Form State
            <div className="relative overflow-hidden">
              {/* Background decoration */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 opacity-50"></div>
              
              <div className="relative p-8 md:p-12">
                {/* Icon */}
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Gift className="w-8 h-8 text-white" />
                </div>

                {/* Badge */}
                <div className="inline-flex items-center space-x-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-bold mb-4 mx-auto">
                  <Sparkles className="w-4 h-4" />
                  <span>⚡ WAIT! Don't Miss This</span>
                </div>

                {/* Headline */}
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
                  Get the <span className="text-emerald-600">$10K Affiliate Blueprint</span> FREE
                </h2>
                
                <p className="text-lg text-gray-600 mb-6 text-center">
                  Before you go... grab our proven step-by-step guide that's helped 2,847+ people start earning affiliate commissions
                </p>

                {/* Benefits */}
                <div className="grid md:grid-cols-2 gap-3 mb-8">
                  {[
                    "Choose products that actually convert",
                    "Get free traffic without spending on ads",
                    "Scale from $0 to $10K/month",
                    "Avoid the 7 biggest beginner mistakes"
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <div className="w-5 h-5 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle2 className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{benefit}</span>
                    </div>
                  ))}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    type="text"
                    placeholder="Your Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-6 text-lg border-2 border-gray-200 focus:border-emerald-500"
                    required
                  />
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-6 text-lg border-2 border-gray-200 focus:border-emerald-500"
                    required
                  />
                  <Button 
                    type="submit"
                    size="lg"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-lg py-6 font-bold shadow-lg hover:shadow-xl transition-all"
                  >
                    <TrendingUp className="w-5 h-5 mr-2" />
                    {loading ? 'Sending...' : 'Send Me The Free Guide'}
                  </Button>
                </form>

                {/* Trust signals */}
                <div className="mt-6 flex items-center justify-center space-x-4 text-xs text-gray-600">
                  <div className="flex items-center space-x-1">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>Instant Delivery</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>No Spam</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>Unsubscribe Anytime</span>
                  </div>
                </div>

                {/* Social proof */}
                <div className="mt-6 text-center">
                  <div className="flex items-center justify-center -space-x-2 mb-2">
                    {[
                      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80",
                      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80",
                      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=80",
                      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&q=80"
                    ].map((img, i) => (
                      <img key={i} src={img} alt="User" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600">
                    <strong>2,847 people</strong> downloaded this week
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
export default ExitIntentPopup;
